import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import DollarIcon from "@redotech/redo-icons/currency-dollar-circle.svg";
import { navigateToBillingConfirmationUrl } from "@redotech/redo-merchant-app-common/billing/billing-confirmation-window";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import { ReloadTeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoTable } from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { OnboardingProps } from "../common";

export const AiSupportActionNeededV2Modal = memo(
  function AiSupportActionNeededV2Modal({
    handleDismiss,
    handleComplete,
  }: OnboardingProps) {
    const reloadTeam = useRequiredContext(ReloadTeamContext);
    const supportRpcClient = useRequiredContext(
      RedoSupportAuthRpcClientContext,
    );

    const pricingData = useLoad(
      () => supportRpcClient?.getBaseSupportPredictedPriceInfo(null) ?? null,
      [supportRpcClient],
    );

    const [signupLoad, doSignup] = useTriggerLoad(async () => {
      const response =
        await supportRpcClient.createSupportAiAndBaseSubscription({});

      navigateToBillingConfirmationUrl({
        confirmationUrl: response.confirmationUrl,
      });

      reloadTeam();
      handleComplete();
      return true;
    });

    const handleAcceptBilling = useHandler(() => {
      amplitude.logEvent("ai-support-upsell-modal-accept-billing-clicked");
      doSignup();
    });

    const handleViewPricingDetails = useHandler(() => {
      amplitude.logEvent("ai-support-upsell-view-pricing-details-clicked");
      window.open(
        "http://help.getredo.com/en/articles/11673216-ai-resolution-pricing",
        "_blank",
      );
    });

    return (
      <RedoModal
        isOpen={!pricingData.pending && !!pricingData.value}
        modalSize="lg"
        onModalCloseRequested={handleComplete}
        primaryButton={{
          text: "Accept billing",
          onClick: handleAcceptBilling,
          pending: signupLoad.pending,
          size: "sm",
        }}
        secondaryButton={{
          text: "View pricing details",
          onClick: () => {
            handleViewPricingDetails();
          },
          size: "sm",
        }}
        title="Accept billing changes"
        TitleIcon={DollarIcon}
      >
        <Flex bgColor="secondary" dir="column" gap="lg" p="2xl" w="full">
          <Text textColor="error">
            If you do not accept the billing changes, you will lose access to
            Redo's AI support tool
          </Text>
          <Text fontSize="sm" fontWeight="thin">
            The support software is free if you're using AI to handle 50%+ of
            your team’s tickets. So this only applies if…
          </Text>
          <Text fontSize="xs" fontWeight="thin" textColor="secondary">
            You only pay for tickets that a human replies to
          </Text>
          <Flex dir="column" fontSize="xs" fontWeight="thin" gap="xs">
            <Text fontSize="xs" pl="lg" textColor="secondary">
              &bull; Spam does not count
            </Text>
            <Text fontSize="xs" fontWeight="thin" pl="lg" textColor="secondary">
              &bull; Auto responders set up with rules do not count
            </Text>
            <Text fontSize="xs" fontWeight="thin" pl="lg" textColor="secondary">
              &bull; This is different from just "tickets replied to" since in
              other tools, this includes automations and the AI.
            </Text>
          </Flex>
          <Text fontSize="xs" textColor="secondary">
            AI-handled tickets not only reduce human work but also make an equal
            number of human tickets free.
          </Text>
          <Text fontSize="xs" textColor="secondary">
            Each ticket resolved by AI is one less human ticket that you have to
            pay for.
          </Text>
          <Flex dir="column" gap="md">
            <Text fontSize="sm" fontWeight="semibold" pb="sm">
              Support software pricing based on monthly usage
            </Text>
            <RedoTable<{ numTickets: number; priceInCents: number }>
              layout={{
                columns: [
                  {
                    key: "tickets",
                    widthMode: { type: "fill" },
                    renderHeaderCell: () => (
                      <Text fontSize="xs">Monthly Tickets</Text>
                    ),
                    renderNormalCell: ({ row, rowIndex }) => (
                      <Flex dir="column" gap="xxs">
                        <Text
                          fontSize="xs"
                          fontWeight={
                            rowIndex === pricingData.value?.currentBucketIndex
                              ? "semibold"
                              : "thin"
                          }
                        >
                          {row.numTickets === 0
                            ? "0"
                            : `Up to ${row.numTickets}`}
                        </Text>
                        {rowIndex === pricingData.value?.currentBucketIndex && (
                          <Text fontSize="xs" fontWeight="thin">
                            Based on last month's usage
                          </Text>
                        )}
                      </Flex>
                    ),
                  },
                  {
                    key: "price",
                    widthMode: { type: "fill" },
                    renderHeaderCell: () => (
                      <Text fontSize="xs">Monthly Price</Text>
                    ),
                    renderNormalCell: ({ row, rowIndex }) => (
                      <Text
                        fontSize="xs"
                        fontWeight={
                          rowIndex === pricingData.value?.currentBucketIndex
                            ? "semibold"
                            : "thin"
                        }
                      >
                        ${(row.priceInCents / 100).toFixed(2)}
                      </Text>
                    ),
                  },
                ],
              }}
              rowsOfData={pricingData.value?.pricingTiers || []}
              size="sm"
            />
          </Flex>
        </Flex>
      </RedoModal>
    );
  },
);
